import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import {
  PaginationDropdown,
  ThinDropdown,
  ThinTextField,
} from "../common/GeneralComponent";
import { fetchData } from "../../utils/functions";
import { logout } from "../../slices/authSlice";
import { useDispatch } from "react-redux";
import GeneralFooter from "../common/GeneralFooter";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { Minus, Plus, Download } from "lucide-react";
import { toast } from "react-toastify";

const ImplementationListing = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [details, setDetails] = useState([]);
  const [rolesData, setRolesData] = useState([]);
  const [search, setSearch] = useState("");
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [filterVillageName, setFilterVillageName] = useState("");
  const [filterFeatureCount, setFilterFeatureCount] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  //toggle filter accordion
  const toggleFilters = () => {
    setIsFiltersVisible((prev) => !prev);
  };

  //  Fetch API data
  const fetchVillages = async () => {
    try {
      const response = await fetchData(
        "implementation-phase-details",
        "GET",
        null,
        {},
        {},
        navigate,
        null,
        dispatch
      );

      if (response.success && Array.isArray(response.data)) {
        const tableData = response.data.map((item) => ({
          village_id: item.village_id,
          village_name: item.village_name || "N/A",
          proposed_feature_count: item.proposed_feature_count
            ? Number(item.proposed_feature_count)
            : 0,
          implemented_feature_count: item.implemented_feature_count
            ? Number(item.implemented_feature_count)
            : 0,
          boq_proposed_id: Array.isArray(item.boq_proposed_id)
            ? item.boq_proposed_id
            : [],
          created_at: item.created_at || item.updated_at || null,
        }));

        const sortedData = (tableData || []).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        setDetails(sortedData);
        setRolesData(sortedData);

        setPagination((prev) => ({
          ...prev,
          totalItems: sortedData.length,
          totalPages: Math.ceil(sortedData.length / prev.pageSize),
        }));
      } else {
        setDetails([]);
        setRolesData([]);
        console.warn("Unexpected response format:", response);
      }
    } catch (error) {
      console.error("Failed to fetch villages:", error);
    }
  };

  useEffect(() => {
    fetchVillages();
  }, [dispatch, navigate]);

  // Options for Village Name dropdown
  const villageOptions = rolesData.map((village) => ({
    label: village.village_name,
    value: village.village_name,
  }));

  //  Filter based on search
  const filteredVillages = rolesData.filter((village) => {
    const matchesSearch =
      village.village_name.toLowerCase().includes(search.toLowerCase()) ||
      village.proposed_feature_count.toString().includes(search) ||
      village.implemented_feature_count.toString().includes(search);

    const matchesVillageFilter = filterVillageName
      ? village.village_name === filterVillageName
      : true;

    const matchesFeatureFilter = filterFeatureCount
      ? village.proposed_feature_count === Number(filterFeatureCount) ||
        village.implemented_feature_count === Number(filterFeatureCount)
      : true;

    return matchesSearch && matchesVillageFilter && matchesFeatureFilter;
  });

  //handle apply
  const handleApply = () => {
    // Basic validation
    if (!filterVillageName && !filterFeatureCount) {
      //toast.error("Please select at least one filter to apply");
      return;
    }

    // Check feature count validity
    if (filterFeatureCount && Number(filterFeatureCount) < 0) {
      toast.error("Feature count cannot be negative");
      return;
    }

    // Apply filters
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  //To export excel
  const handleExport = async () => {
    if (!rolesData || rolesData.length === 0) return;

    try {
      setIsExporting(true);
      toast.info("Preparing export... This may take a moment.");

      const filteredVillageIds =
        filteredVillages?.map((item) => item.village_id) || [];

      const params = new URLSearchParams();
      if (filteredVillageIds.length > 0) {
        params.append("filter_ids", JSON.stringify(filteredVillageIds));
      }

      const BASE_URL = process.env.REACT_APP_BASE_URL;
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const response = await fetch(
        `${BASE_URL}/api/implementation-phase-details/export?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          dispatch(logout());
          navigate("/login");
        }
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Export failed");
        }
        throw new Error(`Export failed with status: ${response.status}`);
      }

      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `implementation_phase_export.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/"/g, "");
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Export completed! Downloaded: ${filename}`);
    } catch (error) {
      console.error("Error during export:", error);
      toast.error(error.message || "Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  //  Pagination logic
  const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
  const paginatedVillages = filteredVillages.slice(
    startIndex,
    startIndex + pagination.pageSize
  );

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: size,
      currentPage: 1,
      totalPages: Math.ceil(filteredVillages.length / size),
    }));
  };

  //  Keep pagination totals synced automatically
  useEffect(() => {
    const totalPages = Math.ceil(filteredVillages.length / pagination.pageSize);
    setPagination((prev) => ({
      ...prev,
      totalItems: filteredVillages.length,
      totalPages,
    }));
  }, [filteredVillages.length, pagination.pageSize]);

  // Table Columns
  const columns = [
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Sr.No</span>
      ),
      cell: (row, index) => <span>{startIndex + index + 1}</span>,
      width: "80px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Village</span>
      ),
      selector: (row) => row.village_name || "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Proposed Feature Count
        </span>
      ),
      selector: (row) => row.proposed_feature_count || 0,
      sortable: true,
      center: true,
      width: "260px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Implemented Feature Count
        </span>
      ),
      selector: (row) => row.implemented_feature_count || 0,
      sortable: true,
      center: true,
      width: "290px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Action</span>
      ),
      cell: (row) => (
        <button
          onClick={() =>
            navigate(`/implementation-phase/manage/${row.village_id}`)
          }
          className="bg-[#137e81] hover:bg-[#0f6b6e] text-white px-3 py-1 rounded-md font-semibold transition-colors duration-200"
        >
          Implementation
        </button>
      ),
      center: true,
    },
  ];
  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "#fff",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  return (
    <div className="w-full">
      <div className="my-4 w-full flex flex-col justify-between space-y-3">
        <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            Implementation Phase Information
          </h1>
          {rolesData.length > 0 && (
            <button
              onClick={() => {
                // If a village filter is applied, find its id
                const village = filterVillageName
                  ? rolesData.find((v) => v.village_name === filterVillageName)
                  : null;

                handleExport(village ? village.village_id : null);
              }}
              disabled={isExporting}
              className={`flex items-center gap-2 px-4 py-2 mb-1 rounded-lg font-semibold transition-all ${
                isExporting
                  ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                  : "bg-[#137e81] text-white hover:bg-[#0f6c6f] hover:scale-105"
              }`}
              title="Export data to Excel"
            >
              <Download size={18} />
              {isExporting ? "Exporting..." : "Export Excel"}
            </button>
          )}
        </div>
        <Accordion expanded={isFiltersVisible} onChange={toggleFilters}>
          <AccordionSummary
            expandIcon={
              isFiltersVisible ? (
                <Minus color="#ffff" size={14} />
              ) : (
                <Plus color="#ffff" size={14} />
              )
            }
            aria-controls="panel1-content"
            id="panel1-header"
            sx={{
              backgroundColor: "#137e81",
              color: "#fff !important",
              "& .MuiAccordionSummary-content": {
                color: "#fff !important",
              },
            }}
          >
            Filters
          </AccordionSummary>

          <AccordionDetails>
            <div className="space-y-4">
              <div className="flex gap-5 w-full">
                {/* Village Name Filter */}
                <div className="flex-1">
                  <ThinDropdown required={false}
                    label="Village Name"
                    value={filterVillageName}
                    onChange={(e) => setFilterVillageName(e.target.value)}
                    options={villageOptions}
                    placeholder="Select Village"
                  />
                </div>

                {/* Feature Count Filter */}
                <div className="flex-1">
                  <ThinTextField isRequired={false}
                    label="Feature Count (Proposed/Implemented)"
                    type="number"
                    value={filterFeatureCount}
                    onChange={(e) => setFilterFeatureCount(e.target.value)}
                    min={0}
                  />
                </div>

                {/* You can keep your existing dropdowns if needed */}
              </div>

              <div className="flex gap-2 mt-4 justify-end">
                <button
                  onClick={() => {
                    setFilterVillageName("");
                    setFilterFeatureCount("");
                  }}
                  className="px-4 py-2 bg-[#be9609] text-white rounded-lg hover:bg-[#a68108] transition-colors"
                >
                  Clear
                </button>

                <button
                  onClick={handleApply}
                  className="px-4 py-2 bg-[#137e81] text-white rounded-lg transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
      <div className="rounded-lg my-[30px] shadow w-full bg-white">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
          <h1 className="text-[0.875rem] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Manage Implementation Phase
          </h1>
        </div>

        <div className="p-[1.25rem] w-full">
          {/* Search + Page size */}
          <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-[6px] w-full">
            <PaginationDropdown
              value={pagination.pageSize}
              onChange={handlePageSizeChange}
              options={[
                { value: 10, label: "10" },
                { value: 20, label: "20" },
                { value: 30, label: "30" },
              ]}
            />

            <input
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
              value={search}
              placeholder="Search villages..."
              type="search"
              className="font-[Light] text-[14px] flex items-center bg-[#f1f4f8] border-[#ebeef2] border-[1px] rounded-md px-3 py-1 outline-none"
            />
          </div>

          {/* DataTable */}
          <DataTable
            columns={columns}
            persistTableHead
            data={paginatedVillages}
            customStyles={customStyles}
            highlightOnHover
            progressPending={false}
          />

          {/* Pagination Footer */}
          <div className="flex md:flex-row flex-col justify-between items-center">
            <GeneralFooter
              pagination={pagination}
              handlePageChange={handlePageChange}
              handlePageSizeChange={handlePageSizeChange}
              dataLength={filteredVillages.length}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImplementationListing;
